import React, { useCallback, useState } from 'react';
import { InvoiceDetailProps } from '..';
import {
  PivotDisplay,
  PivotMenuItemProps,
} from 'common/components/PivotDisplay';
import { GlobalActions } from 'common/constants';
import { Pivot, PivotItem, Stack } from '@fluentui/react';
import { useStyles } from './index.styles';
import { PivotCustomRender } from 'common/components/PivotComponent';
import { Files } from './Files';
import { useQuery } from '@apollo/client';
import {
  ListInvoiceNotes,
  ListInvoiceNotesVariables,
} from 'common/graphql/__generated__/ListInvoiceNotes';
import { Notes } from './Notes';
import { HistoryActionMenuView } from 'common/components/History';
import {
  MenuPropertiesCount,
  getUpdatedPivotActionItems,
} from 'common/components/Modals/utils';
import { Tags } from './Tags';
import { pivotActionItemsTags as pivotActionItems } from 'common/components/ActionMenu/PivotActionItems';

import ENTITY_NOTES_LIST from '../../../../../graphql/ListInvoiceNotes.graphql';

type ActionMenuProps = Pick<InvoiceDetailProps, 'invoice'>;

export const ActionMenu: React.FC<ActionMenuProps> = ({ invoice }) => {
  const styles = useStyles();
  const {
    id,
    entityDocumentsByEntityId,
    notesByEntityId,
    invoiceHistoriesByEntityId,
    entityTagsByEntityId,
  } = { ...invoice };
  let updatedPivotActionItems: PivotMenuItemProps[] = [];
  const [isMenuExpanded, setIsMenuExpanded] = useState<boolean>(true);
  const [selectedKey, setSelectedKey] = useState<string>(GlobalActions.attach);

  const { data: entityNotesData, refetch: entityNotesListRefetch } = useQuery<
    ListInvoiceNotes,
    ListInvoiceNotesVariables
  >(ENTITY_NOTES_LIST, {
    variables: {
      id,
    },
    skip: !id,
  });

  const getEntityNotes = () => {
    const variables: ListInvoiceNotesVariables = {
      id,
    };
    entityNotesListRefetch(variables);
  };

  const getEntityNotesMemo = useCallback(getEntityNotes, [id!]);

  const menuCountArray: MenuPropertiesCount[] = [
    {
      name: 'Files',
      iconName: 'Attach',
      count: entityDocumentsByEntityId.nodes.length || 0,
    },
    {
      name: 'Notes',
      iconName: 'Page',
      count: notesByEntityId.nodes.length || 0,
    },
    {
      name: 'History',
      iconName: 'History',
      count: invoiceHistoriesByEntityId.nodes.length || 0,
    },
    {
      name: 'Tags',
      iconName: 'Tag',
      count: entityTagsByEntityId.nodes.length || 0,
    },
  ];

  updatedPivotActionItems = invoice
    ? getUpdatedPivotActionItems(menuCountArray, pivotActionItems)
    : (updatedPivotActionItems = pivotActionItems);

  const getSelectedSection = (key: string) => {
    switch (key) {
      case GlobalActions.attach:
        return <Files fileListData={entityDocumentsByEntityId!} />;
      case GlobalActions.notes:
        return (
          <Notes
            invoiceId={id}
            data={entityNotesData?.invoice?.notesByEntityId.nodes!}
            listRefreshRequest={getEntityNotesMemo}
          />
        );
      case GlobalActions.history:
        return (
          <HistoryActionMenuView
            moduleName="Transaction Signing"
            historyData={invoiceHistoriesByEntityId?.nodes || []}
          />
        );
      case GlobalActions.tags:
        return <Tags invoiceId={id} />;
      default:
        return null;
    }
  };

  return (
    <Stack tokens={{ childrenGap: 20 }}>
      <Stack tokens={{ padding: '10px 20px' }}>
        {isMenuExpanded ? (
          <Stack>
            <Stack horizontalAlign="space-between" horizontal>
              <Stack horizontal className={styles.centerAlign}>
                <Pivot
                  selectedKey={selectedKey}
                  onLinkClick={(item: PivotItem | undefined) => {
                    setSelectedKey(item?.props.itemKey || GlobalActions.attach);
                  }}
                >
                  {updatedPivotActionItems.map((item, index) => {
                    return (
                      <PivotItem
                        key={index}
                        itemKey={item.itemKey}
                        itemIcon={item.iconName}
                        headerText={item.name}
                        itemCount={item.count}
                        onRenderItemLink={PivotCustomRender}
                      />
                    );
                  })}
                </Pivot>
              </Stack>
            </Stack>
            <Stack>{getSelectedSection(selectedKey)}</Stack>
          </Stack>
        ) : (
          <Stack horizontalAlign="space-between" horizontal>
            <Stack horizontal verticalAlign="center">
              <Stack horizontal verticalAlign="center">
                {updatedPivotActionItems.map((item, index) => {
                  return (
                    <PivotDisplay
                      key={index}
                      {...item}
                      onPivotClick={() => {
                        setSelectedKey(item.itemKey!);
                        setIsMenuExpanded(true);
                      }}
                    />
                  );
                })}
              </Stack>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
